import axios, { AxiosInstance } from 'axios'
import CryptoJS from 'crypto-js'
import { DateTime } from 'luxon'
import Cookies from 'universal-cookie/es6'
import isEmpty from 'lodash/isEmpty'

import { Environment } from "../environment/app-settings"
import { ConsentFetchModel, IAppConsentsModel } from '../models/settings/consent.fetch.model'
import { ApplicantDataFieldNames, ApplicantDataIncomeFieldNames, IAppConsentsModelToSend, IFormSubmissionModel, ILoanApplicantDataAddressModel, ILoanApplicantDataContactModel, IAdditionalLoanDataModel } from '../models/form/IFormSubmitionModel'
import { AddresTypeEnum } from '../formDefinitions/AddressInfoForm/AddressTypes'
import { HttpClient } from '../common/http-client'
import { STORAGEKEYS } from '../common/keys'
import { BasicInfoFieldsNames } from '../formDefinitions/BasicInfo/BasicInfoFields'
import { AdditionalFormNames, AllAdditionalFieldsNames, AllBasedFieldsNames, FormNames } from '../models/form/IFormDefinition'
import { IncomeTypeEnum } from '../formDefinitions/IncomeInfoForm/IncomeTypes'
import { TIncomeFieldsValues } from '../formDefinitions/IncomeInfoForm/IncomeInfoFields'
import { AllFormsState, IFieldsDataStateValue } from '../store/types'


export interface ISubmitFormResponse {
  alphaId: string;
}


//========================== HELPERS ================================
/**
 * Mapuje zgody z aplikacji do modelu wymaganego przez Backend
 * 
 * @param appConsents - Model zgód używany w stanie aplikacji
 * @returns {IAppConsentsModelToSend[]} - Tablica zmapowanych na model z Backendu zgód
 */
const mapConsentsToModel = (appConsents: IAppConsentsModel[]): IAppConsentsModelToSend[] => {
  if(!isEmpty(appConsents)){
    const appConsentsToSend: IAppConsentsModelToSend[] = [];

    for (const appConsent of appConsents) {
      appConsentsToSend.push({
        id: appConsent.consentId,
        text: appConsent.text,
        isConsent: appConsent.isChecked
      })
    }
    return appConsentsToSend
  }

  else return []
}

/**
 * Mapuje dane z pól z formularza z adresem na model przyjmowany przez Backend. W momencie gdy użytkownik
 * zaznaczył adres korespondencyjny, do tablicy adresów wrzucane są dwa obiekty, gdy zaznaczył tylko adres formalny
 * jest on przepisywany do drugiego obiektu jako korespondencyjny.

 * @param addressInfo Obiekt z adresami używany w stanie aplikacji
 * @returns {ILoanApplicantDataAddressModel[]} Tablica adresów zmapowanych na model z Backendu
 */
const mapAddresses = (addressInfo: Record<string, IFieldsDataStateValue>, hasAdditionalAddress: boolean): ILoanApplicantDataAddressModel[] => {
   const formalAddress = {
      [ApplicantDataFieldNames.type]: AddresTypeEnum.formal,
      [ApplicantDataFieldNames.street]: addressInfo[AllBasedFieldsNames.Street].value as string,
      [ApplicantDataFieldNames.houseNumber]: addressInfo[AllBasedFieldsNames.HouseNumber].value as string,
      [ApplicantDataFieldNames.apartmentNumber]: addressInfo[AllBasedFieldsNames.ApartamentNumber].value as string,
      [ApplicantDataFieldNames.postalCode]: addressInfo[AllBasedFieldsNames.PostalCode].value as string,
      [ApplicantDataFieldNames.city]: addressInfo[AllBasedFieldsNames.City].value as string,
    }
  
    const correspondenceAddress = hasAdditionalAddress
      ? {
          [ApplicantDataFieldNames.type]: AddresTypeEnum.correspondence,
          [ApplicantDataFieldNames.street]: addressInfo[AllAdditionalFieldsNames.CorrespondenceStreet].value as string,
          [ApplicantDataFieldNames.houseNumber]: addressInfo[AllAdditionalFieldsNames.CorrespondenceHouseNumber].value as string,
          [ApplicantDataFieldNames.apartmentNumber]: addressInfo[AllAdditionalFieldsNames.CorrespondenceApartamentNumber].value as string,
          [ApplicantDataFieldNames.postalCode]: addressInfo[AllAdditionalFieldsNames.CorrespondencePostalCode].value as string,
          [ApplicantDataFieldNames.city]: addressInfo[AllAdditionalFieldsNames.CorrespondenceCity].value as string,
        }
      : { ...formalAddress, [ApplicantDataFieldNames.type]: AddresTypeEnum.correspondence }
  
    return [formalAddress, correspondenceAddress]
}


// /**
//  * Mapuje dane z pól z formularza z dochodem na model przyjmowany przez Backend. W momencie gdy użytkownik
//  * zaznaczył dodatkowy dochód, do tablicy dochodów wrzucane są dwa obiekty, z dwoma dochodami. 

//  * @param incomeInfo Obiekt z dochodami używany w stanie aplikacji
//  * @returns {TIncomeFieldsValues[]} Tablica dochodów zmapowanych na model z Backendu
//  */
const mapIncomeToModel = (incomeInfo: Record<string, IFieldsDataStateValue>, hasAdditionalIncome: boolean): TIncomeFieldsValues[] => {
    const incomeModel: TIncomeFieldsValues[] = []
    const SOCIAL_BENEFIT_VALUE = 800 //Wartość dla źródła dochodu 800+

    let basicIncomeFields: {[key: string]: string | number | boolean} = {}
    let additionalIncomeFields: {[key: string]: string | number | boolean} = {}
    let benefitsIncomeFields: {[key: string]: string | number | boolean} = {}
    let grantsIncomeFields: {[key: string]: string | number | boolean} = {}

    // ========================================= MAPOWANIE PODSTAWOWEGO ŹRÓDŁA DOCHODU =========================================
    switch(incomeInfo[AllBasedFieldsNames.MainIncomeType].value){
      case IncomeTypeEnum.Retirement: { // >>> EMERYTURA
        basicIncomeFields = {
          [ApplicantDataIncomeFieldNames.type]: incomeInfo[AllBasedFieldsNames.MainIncomeType].value,
          [ApplicantDataIncomeFieldNames.incomePerMonth]: incomeInfo[AllBasedFieldsNames.IncomePerMonthNetto].value,
          [ApplicantDataIncomeFieldNames.incomeReceivedFromDate]: DateTime.fromFormat(incomeInfo[AllBasedFieldsNames.BenefitRecivedFromDate].value as string, "dd/MM/yyyy").toUTC().toISO() as string,
          [ApplicantDataIncomeFieldNames.incomeReceivedDay]: incomeInfo[AllBasedFieldsNames.IncomeReceivedDay].value,         
        }

        break;
      }

      case IncomeTypeEnum.Pension: { // >>> RENTA
        basicIncomeFields = {
          [ApplicantDataIncomeFieldNames.type]: incomeInfo[AllBasedFieldsNames.MainIncomeType].value,
          [ApplicantDataIncomeFieldNames.incomePerMonth]: incomeInfo[AllBasedFieldsNames.IncomePerMonthNetto].value,
          [ApplicantDataIncomeFieldNames.incomeReceivedFromDate]: DateTime.fromFormat(incomeInfo[AllBasedFieldsNames.BenefitRecivedFromDate].value as string, "dd/MM/yyyy").toUTC().toISO() as string,
          [ApplicantDataIncomeFieldNames.incomeReceivedToDate]: incomeInfo[AllBasedFieldsNames.IsBenefitIndefinitely].value ? DateTime.fromFormat(incomeInfo[AllBasedFieldsNames.BenefitRecivedToDate].value as string, "dd/MM/yyyy").toUTC().toISO() as string : '',
          [ApplicantDataIncomeFieldNames.incomeReceivedDay]: incomeInfo[AllBasedFieldsNames.IncomeReceivedDay].value,       
        }
        break;
      }
      case IncomeTypeEnum.Contract: { // >>> KONTRAKT
        basicIncomeFields = {
          [ApplicantDataIncomeFieldNames.type]: incomeInfo[AllBasedFieldsNames.MainIncomeType].value,
          [ApplicantDataIncomeFieldNames.incomePerMonth]: incomeInfo[AllBasedFieldsNames.IncomePerMonthNetto].value,
          [ApplicantDataIncomeFieldNames.incomeReceivedDay]: incomeInfo[AllBasedFieldsNames.IncomeReceivedDay].value,
          [ApplicantDataIncomeFieldNames.incomeReceivedFromDate]: DateTime.fromFormat(incomeInfo[AllBasedFieldsNames.EmploymentFromDate].value as string, "dd/MM/yyyy").toUTC().toISO() as string,
          [ApplicantDataIncomeFieldNames.incomeReceivedToDate]: incomeInfo[AllBasedFieldsNames.IsEmploymentIndefinitely].value ? DateTime.fromFormat(incomeInfo[AllBasedFieldsNames.EmploymentToDate].value as string, "dd/MM/yyyy").toUTC().toISO() as string : '',
          [ApplicantDataIncomeFieldNames.companyName]: incomeInfo[AllBasedFieldsNames.WorkplaceName].value,            
          [ApplicantDataIncomeFieldNames.NIP]: incomeInfo[AllBasedFieldsNames.WorkplaceNIP].value,           
        }
        break;
      }
      case IncomeTypeEnum.SoleProprietorship: { // >>> DZIAŁALNOŚĆ GOSPODARCZA
        basicIncomeFields = {
          [ApplicantDataIncomeFieldNames.type]: incomeInfo[AllBasedFieldsNames.MainIncomeType].value,
          [ApplicantDataIncomeFieldNames.incomePerMonth]: incomeInfo[AllBasedFieldsNames.IncomePerMonthNetto].value,
          [ApplicantDataIncomeFieldNames.incomeReceivedDay]: incomeInfo[AllBasedFieldsNames.IncomeReceivedDay].value,
          [ApplicantDataIncomeFieldNames.incomeReceivedFromDate]: DateTime.fromFormat(incomeInfo[AllBasedFieldsNames.SoleProprietorshipFromDate].value as string, "dd/MM/yyyy").toUTC().toISO() as string,
          [ApplicantDataIncomeFieldNames.companyName]: incomeInfo[AllBasedFieldsNames.CompanyName].value,            
          [ApplicantDataIncomeFieldNames.NIP]: incomeInfo[AllBasedFieldsNames.NIP].value,           
        }
        break;
      }
      case IncomeTypeEnum.Farmer: { // >>> DZIAŁALNOŚĆ ROLNICZA
        basicIncomeFields = {
          [ApplicantDataIncomeFieldNames.type]: incomeInfo[AllBasedFieldsNames.MainIncomeType].value,
          [ApplicantDataIncomeFieldNames.incomePerMonth]: incomeInfo[AllBasedFieldsNames.IncomePerMonthNetto].value,
          [ApplicantDataIncomeFieldNames.incomeReceivedDay]: incomeInfo[AllBasedFieldsNames.IncomeReceivedDay].value,
          [ApplicantDataIncomeFieldNames.incomeReceivedFromDate]: DateTime.fromFormat(incomeInfo[AllBasedFieldsNames.FarmRunningFromDate].value as string, "dd/MM/yyyy").toUTC().toISO() as string,          
        }
        
        if(incomeInfo[AllBasedFieldsNames.HasUnionGrants].value !== false){ // Wartość false w tym przypadku oznacza że użytkownik 
          const unionGrantAmount: number = parseInt(incomeInfo[AllBasedFieldsNames.UnionGrantAmount].value as string)

          grantsIncomeFields = {
            [ApplicantDataIncomeFieldNames.type]: IncomeTypeEnum.UnionGrants,
            [ApplicantDataIncomeFieldNames.incomePerMonth]: unionGrantAmount / 12, //Dzielimy na 12 aby uzyskać dochód miesięczny
            [ApplicantDataIncomeFieldNames.incomeReceivedDay]: incomeInfo[AllBasedFieldsNames.IncomeReceivedDay].value as string,
          }
        }
        break;
      }
      case IncomeTypeEnum.CommissionAgreement: { // >>> UMOWA ZLECENIE
        basicIncomeFields = {
          [ApplicantDataIncomeFieldNames.type]: incomeInfo[AllBasedFieldsNames.MainIncomeType].value,
          [ApplicantDataIncomeFieldNames.incomePerMonth]: incomeInfo[AllBasedFieldsNames.IncomePerMonthNetto].value,
          [ApplicantDataIncomeFieldNames.incomeReceivedDay]: incomeInfo[AllBasedFieldsNames.IncomeReceivedDay].value,
          [ApplicantDataIncomeFieldNames.incomeReceivedFromDate]: DateTime.fromFormat(incomeInfo[AllBasedFieldsNames.EmploymentFromDate].value as string, "dd/MM/yyyy").toUTC().toISO() as string,
          [ApplicantDataIncomeFieldNames.incomeReceivedToDate]: incomeInfo[AllBasedFieldsNames.IsEmploymentIndefinitely].value ? DateTime.fromFormat(incomeInfo[AllBasedFieldsNames.EmploymentToDate].value as string, "dd/MM/yyyy").toUTC().toISO() as string : '',
          [ApplicantDataIncomeFieldNames.companyName]: incomeInfo[AllBasedFieldsNames.WorkplaceName].value,            
          [ApplicantDataIncomeFieldNames.NIP]: incomeInfo[AllBasedFieldsNames.WorkplaceNIP].value,          
        }
        break;
      }
      case IncomeTypeEnum.EmploymentContract: { // >>> UMOWA O PRACĘ
        basicIncomeFields = {
          [ApplicantDataIncomeFieldNames.type]: incomeInfo[AllBasedFieldsNames.MainIncomeType].value,
          [ApplicantDataIncomeFieldNames.incomePerMonth]: incomeInfo[AllBasedFieldsNames.IncomePerMonthNetto].value,
          [ApplicantDataIncomeFieldNames.incomeReceivedDay]: incomeInfo[AllBasedFieldsNames.IncomeReceivedDay].value,
          [ApplicantDataIncomeFieldNames.incomeReceivedFromDate]: DateTime.fromFormat(incomeInfo[AllBasedFieldsNames.EmploymentFromDate].value as string, "dd/MM/yyyy").toUTC().toISO() as string,
          [ApplicantDataIncomeFieldNames.incomeReceivedToDate]: incomeInfo[AllBasedFieldsNames.IsEmploymentIndefinitely].value ? DateTime.fromFormat(incomeInfo[AllBasedFieldsNames.EmploymentToDate].value as string, "dd/MM/yyyy").toUTC().toISO() as string : '',
          [ApplicantDataIncomeFieldNames.companyName]: incomeInfo[AllBasedFieldsNames.WorkplaceName].value,            
          [ApplicantDataIncomeFieldNames.NIP]: incomeInfo[AllBasedFieldsNames.WorkplaceNIP].value,                     
        }
        break;
      }
      case IncomeTypeEnum.Rental: { // >>> NAJEM
        basicIncomeFields = {
          [ApplicantDataIncomeFieldNames.type]: incomeInfo[AllBasedFieldsNames.MainIncomeType].value,
          [ApplicantDataIncomeFieldNames.incomePerMonth]: incomeInfo[AllBasedFieldsNames.IncomePerMonthNetto].value,
          [ApplicantDataIncomeFieldNames.incomeReceivedFromDate]: DateTime.fromFormat(incomeInfo[AllBasedFieldsNames.RentalFromDate].value as string, "dd/MM/yyyy").toUTC().toISO() as string,
          [ApplicantDataIncomeFieldNames.incomeReceivedToDate]: DateTime.fromFormat(incomeInfo[AllBasedFieldsNames.RentalToDate].value as string, "dd/MM/yyyy").toUTC().toISO() as string,
          [ApplicantDataIncomeFieldNames.incomeReceivedDay]: incomeInfo[AllBasedFieldsNames.IncomeReceivedDay].value,          
        }
        break;
      }
      default: break;
    }

    // ========================================= MAPOWANIE DODATKOWEGO ŹRÓDŁA DOCHODU =========================================
    if(hasAdditionalIncome){
      switch(incomeInfo[AllAdditionalFieldsNames.AdditionalIncomeType].value){
        case IncomeTypeEnum.Retirement: { // >>> EMERYTURA
          additionalIncomeFields = {
            [ApplicantDataIncomeFieldNames.type]: incomeInfo[AllAdditionalFieldsNames.AdditionalIncomeType].value,
            [ApplicantDataIncomeFieldNames.incomePerMonth]: incomeInfo[AllAdditionalFieldsNames.AdditionalIncomePerMonthNetto].value,
            [ApplicantDataIncomeFieldNames.incomeReceivedFromDate]: DateTime.fromFormat(incomeInfo[AllAdditionalFieldsNames.AdditionalIncomeBenefitRecivedFromDate].value as string, "dd/MM/yyyy").toUTC().toISO() as string,
            [ApplicantDataIncomeFieldNames.incomeReceivedDay]: incomeInfo[AllAdditionalFieldsNames.AdditionalIncomeIncomeReceivedDay].value,         
          }
          break;
        }
  
        case IncomeTypeEnum.Pension: { // >>> RENTA
          additionalIncomeFields = {
            [ApplicantDataIncomeFieldNames.type]: incomeInfo[AllAdditionalFieldsNames.AdditionalIncomeType].value,
            [ApplicantDataIncomeFieldNames.incomePerMonth]: incomeInfo[AllAdditionalFieldsNames.AdditionalIncomePerMonthNetto].value,
            [ApplicantDataIncomeFieldNames.incomeReceivedFromDate]: DateTime.fromFormat(incomeInfo[AllAdditionalFieldsNames.AdditionalIncomeBenefitRecivedFromDate].value as string, "dd/MM/yyyy").toUTC().toISO() as string,
            [ApplicantDataIncomeFieldNames.incomeReceivedDay]: incomeInfo[AllAdditionalFieldsNames.AdditionalIncomeIncomeReceivedDay].value,  
            [ApplicantDataIncomeFieldNames.incomeReceivedToDate]: incomeInfo[AllAdditionalFieldsNames.AdditionalIncomeIsBenefitIndefinitely].value ? DateTime.fromFormat(incomeInfo[AllAdditionalFieldsNames.AdditionalIncomeBenefitRecivedToDate].value as string, "dd/MM/yyyy").toUTC().toISO() as string : '',
          }
          break;
        }
        case IncomeTypeEnum.Contract: { // >>> KONTRAKT
          additionalIncomeFields = {
            [ApplicantDataIncomeFieldNames.type]: incomeInfo[AllAdditionalFieldsNames.AdditionalIncomeType].value,
            [ApplicantDataIncomeFieldNames.incomePerMonth]: incomeInfo[AllAdditionalFieldsNames.AdditionalIncomePerMonthNetto].value,
            [ApplicantDataIncomeFieldNames.incomeReceivedFromDate]: DateTime.fromFormat(incomeInfo[AllAdditionalFieldsNames.AdditionalIncomeEmploymentFromDate].value as string, "dd/MM/yyyy").toUTC().toISO() as string,
            [ApplicantDataIncomeFieldNames.incomeReceivedDay]: incomeInfo[AllAdditionalFieldsNames.AdditionalIncomeIncomeReceivedDay].value,  
            [ApplicantDataIncomeFieldNames.incomeReceivedToDate]: incomeInfo[AllAdditionalFieldsNames.AdditionalIncomeIsEmploymentIndefinitely].value ? DateTime.fromFormat(incomeInfo[AllAdditionalFieldsNames.AdditionalIncomeEmploymentToDate].value as string, "dd/MM/yyyy").toUTC().toISO() as string : '',
            [ApplicantDataIncomeFieldNames.companyName]: incomeInfo[AllAdditionalFieldsNames.AdditionalIncomeWorkplaceName].value,            
            [ApplicantDataIncomeFieldNames.NIP]: incomeInfo[AllAdditionalFieldsNames.AdditionalIncomeWorkplaceNIP].value,      
          }  
          break;
        }
        case IncomeTypeEnum.SoleProprietorship: { // >>> DZIAŁALNOŚĆ GOSPODARCZA
          additionalIncomeFields = {
            [ApplicantDataIncomeFieldNames.type]: incomeInfo[AllAdditionalFieldsNames.AdditionalIncomeType].value,
            [ApplicantDataIncomeFieldNames.incomePerMonth]: incomeInfo[AllAdditionalFieldsNames.AdditionalIncomePerMonthNetto].value,
            [ApplicantDataIncomeFieldNames.incomeReceivedFromDate]: DateTime.fromFormat(incomeInfo[AllAdditionalFieldsNames.AdditionalIncomeSoleProprietorshipFromDate].value as string, "dd/MM/yyyy").toUTC().toISO() as string,
            [ApplicantDataIncomeFieldNames.incomeReceivedDay]: incomeInfo[AllAdditionalFieldsNames.AdditionalIncomeIncomeReceivedDay].value,  
            [ApplicantDataIncomeFieldNames.companyName]: incomeInfo[AllAdditionalFieldsNames.AdditionalIncomeCompanyName].value,            
            [ApplicantDataIncomeFieldNames.NIP]: incomeInfo[AllAdditionalFieldsNames.AdditionalIncomeNIP].value,      
          }    
          break;
        }
        case IncomeTypeEnum.Farmer: { // >>> DZIAŁALNOŚĆ ROLNICZA
          additionalIncomeFields = {
            [ApplicantDataIncomeFieldNames.type]: incomeInfo[AllAdditionalFieldsNames.AdditionalIncomeType].value,
            [ApplicantDataIncomeFieldNames.incomePerMonth]: incomeInfo[AllAdditionalFieldsNames.AdditionalIncomePerMonthNetto].value,
            [ApplicantDataIncomeFieldNames.incomeReceivedFromDate]: DateTime.fromFormat(incomeInfo[AllAdditionalFieldsNames.AdditionalIncomeFarmRunningFromDate].value as string, "dd/MM/yyyy").toUTC().toISO() as string,
            [ApplicantDataIncomeFieldNames.incomeReceivedDay]: incomeInfo[AllAdditionalFieldsNames.AdditionalIncomeIncomeReceivedDay].value,  
          } 

          
          if(incomeInfo[AllAdditionalFieldsNames.AdditionalIncomeHasUnionGrants].value){
            const unionGrantAmount: number = parseInt(incomeInfo[AllAdditionalFieldsNames.AdditionalIncomeUnionGrantAmount].value as string)

            grantsIncomeFields = {
              [ApplicantDataIncomeFieldNames.type]: IncomeTypeEnum.UnionGrants,
              [ApplicantDataIncomeFieldNames.incomePerMonth]: unionGrantAmount / 12, //Dzielimy na 12 aby uzyskać dochód miesięczny
              [ApplicantDataIncomeFieldNames.incomeReceivedDay]: incomeInfo[AllAdditionalFieldsNames.AdditionalIncomeIncomeReceivedDay].value,  
            }
          }
          break;
        }
        case IncomeTypeEnum.CommissionAgreement: { // >>> UMOWA ZLECENIE
          additionalIncomeFields = {
            [ApplicantDataIncomeFieldNames.type]: incomeInfo[AllAdditionalFieldsNames.AdditionalIncomeType].value,
            [ApplicantDataIncomeFieldNames.incomePerMonth]: incomeInfo[AllAdditionalFieldsNames.AdditionalIncomePerMonthNetto].value,
            [ApplicantDataIncomeFieldNames.incomeReceivedFromDate]: DateTime.fromFormat(incomeInfo[AllAdditionalFieldsNames.AdditionalIncomeEmploymentFromDate].value as string, "dd/MM/yyyy").toUTC().toISO() as string,
            [ApplicantDataIncomeFieldNames.incomeReceivedDay]: incomeInfo[AllAdditionalFieldsNames.AdditionalIncomeIncomeReceivedDay].value,  
            [ApplicantDataIncomeFieldNames.incomeReceivedToDate]: incomeInfo[AllAdditionalFieldsNames.AdditionalIncomeIsEmploymentIndefinitely].value ? DateTime.fromFormat(incomeInfo[AllAdditionalFieldsNames.AdditionalIncomeEmploymentToDate].value as string, "dd/MM/yyyy").toUTC().toISO() as string : '',
            [ApplicantDataIncomeFieldNames.companyName]: incomeInfo[AllAdditionalFieldsNames.AdditionalIncomeWorkplaceName].value,            
            [ApplicantDataIncomeFieldNames.NIP]: incomeInfo[AllAdditionalFieldsNames.AdditionalIncomeWorkplaceNIP].value,      
          } 
          break;
        }
        case IncomeTypeEnum.EmploymentContract: { // >>> UMOWA O PRACĘ
          additionalIncomeFields = {
            [ApplicantDataIncomeFieldNames.type]: incomeInfo[AllAdditionalFieldsNames.AdditionalIncomeType].value,
            [ApplicantDataIncomeFieldNames.incomePerMonth]: incomeInfo[AllAdditionalFieldsNames.AdditionalIncomePerMonthNetto].value,
            [ApplicantDataIncomeFieldNames.incomeReceivedFromDate]: DateTime.fromFormat(incomeInfo[AllAdditionalFieldsNames.AdditionalIncomeEmploymentFromDate].value as string, "dd/MM/yyyy").toUTC().toISO() as string,
            [ApplicantDataIncomeFieldNames.incomeReceivedDay]: incomeInfo[AllAdditionalFieldsNames.AdditionalIncomeIncomeReceivedDay].value,  
            [ApplicantDataIncomeFieldNames.incomeReceivedToDate]: incomeInfo[AllAdditionalFieldsNames.AdditionalIncomeIsEmploymentIndefinitely].value ? DateTime.fromFormat(incomeInfo[AllAdditionalFieldsNames.AdditionalIncomeEmploymentToDate].value as string, "dd/MM/yyyy").toUTC().toISO() as string  : '',
            [ApplicantDataIncomeFieldNames.companyName]: incomeInfo[AllAdditionalFieldsNames.AdditionalIncomeWorkplaceName].value,            
            [ApplicantDataIncomeFieldNames.NIP]:incomeInfo[AllAdditionalFieldsNames.AdditionalIncomeWorkplaceNIP].value,      
          } 
          break;
        }
        case IncomeTypeEnum.Rental: { // >>> NAJEM
          additionalIncomeFields = {
            [ApplicantDataIncomeFieldNames.type]: incomeInfo[AllAdditionalFieldsNames.AdditionalIncomeType].value,
            [ApplicantDataIncomeFieldNames.incomePerMonth]: incomeInfo[AllAdditionalFieldsNames.AdditionalIncomePerMonthNetto].value,
            [ApplicantDataIncomeFieldNames.incomeReceivedFromDate]: DateTime.fromFormat(incomeInfo[AllAdditionalFieldsNames.AdditionalIncomeRentalFromDate].value as string, "dd/MM/yyyy").toUTC().toISO() as string,
            [ApplicantDataIncomeFieldNames.incomeReceivedDay]: incomeInfo[AllAdditionalFieldsNames.AdditionalIncomeIncomeReceivedDay].value,  
            [ApplicantDataIncomeFieldNames.incomeReceivedToDate]: DateTime.fromFormat(incomeInfo[AllAdditionalFieldsNames.AdditionalIncomeRentalToDate].value as string, "dd/MM/yyyy").toUTC().toISO() as string,
          }
          break;
        }
        default: break;
      }
    }

    if(isEmpty(additionalIncomeFields)){ //Sytuacja gdy podano tylko jedno źródło dochodu. Tablica z dodatkowymi źródłami jest pusta
      incomeModel.push(basicIncomeFields)
    }
    else {
      incomeModel.push(basicIncomeFields) //Sytuacja gdy podano dwa źródła dochodu - podstawowe i dadatkowe
      incomeModel.push(additionalIncomeFields)
    }

    if(!isEmpty(grantsIncomeFields)){ // Jeśli obiekt nie jest pusty, wysyłamy jako osobne źródło dochodu 
      incomeModel.push(grantsIncomeFields)
    }

    const socialBenefitsAmount: number = parseInt(incomeInfo[AllBasedFieldsNames.SocialBenefitsNumber].value as string)

    
    

    if(socialBenefitsAmount !== 0){
      benefitsIncomeFields = {//Dodajemy dochody z 800+ jako osobne źródło dochodu.
        [ApplicantDataIncomeFieldNames.type]: IncomeTypeEnum.SocialBenefit as string,
        [ApplicantDataIncomeFieldNames.incomePerMonth]: isNaN(socialBenefitsAmount)? 0 : socialBenefitsAmount * SOCIAL_BENEFIT_VALUE,
        [ApplicantDataIncomeFieldNames.incomeReceivedDay]: incomeInfo[AllBasedFieldsNames.IncomeReceivedDay].value,         
      }
      
      incomeModel.push(benefitsIncomeFields)
    }
    
    return incomeModel
}

/**
 * Główna funkcja spinająca pozostałe funkcje pomocnicze oraz towrząca obiekt gotowy do wysłania do Backendu
 * 
 * @param formState Aktualny stan wszystkich kontrolek w aplikacji
 * @param incomeInfoValues Aktualny obiekt ze stanem kontrolek z formularza z dochodami
 * @returns {ILoanApplicantDataModel} Kompletny obiekt wniosku zgodny z modelem danych z Backendu.
 */
const mapApplicantDataToModel = (formState: AllFormsState): any => {

//   const canSubmitForm = validateFieldsBeforeSubmition(formState)

//   if(!canSubmitForm) { // W przypadku gdy któreś z pól formularza nie zostanie poprawnie zwalidowane, rzucamy błędem i nie pozwalamy złożyć wniosku do Backendu.
//     throw new Error("Formularz posiada błędy walidacyjne. Nie udało się poprawnie złożyć wniosku")
//   }

  // Obiekt z danymi kontaktowymi użytkownika
  const contact: ILoanApplicantDataContactModel = {
    [ApplicantDataFieldNames.phone]: formState[FormNames.BasicInfoForm].fieldsData[BasicInfoFieldsNames.PhoneNumber].value  as string,
    [ApplicantDataFieldNames.userEmail]: formState[FormNames.BasicInfoForm].fieldsData[BasicInfoFieldsNames.ConfirmEmail].value as string
  }

  return {
    [ApplicantDataFieldNames.name]: formState[FormNames.BasicInfoForm].fieldsData[AllBasedFieldsNames.FirstName].value,
    [ApplicantDataFieldNames.surname]: formState[FormNames.BasicInfoForm].fieldsData[AllBasedFieldsNames.Surname].value,
    [ApplicantDataFieldNames.pesel]: formState[FormNames.BasicInfoForm].fieldsData[AllBasedFieldsNames.Pesel].value,
    [ApplicantDataFieldNames.personalId]: formState[FormNames.ExtendedInfoForm].fieldsData[AllBasedFieldsNames.PersonalId].value,
    [ApplicantDataFieldNames.personalIdRelease]: DateTime.fromFormat(formState[FormNames.ExtendedInfoForm].fieldsData[AllBasedFieldsNames.PersonalIdRelease].value as string, "dd/MM/yyyy").toUTC().toISO() as string,
    [ApplicantDataFieldNames.personalIdValidity]:DateTime.fromFormat(formState[FormNames.ExtendedInfoForm].fieldsData[AllBasedFieldsNames.PersonalIdRelease].value as string, "dd/MM/yyyy").plus({ years: 10 }).toUTC().toISO() as string,
    [ApplicantDataFieldNames.maritalStatus]: formState[FormNames.ExtendedInfoForm].fieldsData[AllBasedFieldsNames.MaritalStatus].value,
    [ApplicantDataFieldNames.education]: formState[FormNames.ExtendedInfoForm].fieldsData[AllBasedFieldsNames.Education].value,
    [ApplicantDataFieldNames.costOfLiving]: parseInt(formState[FormNames.IncomeInfoForm].fieldsData[AllBasedFieldsNames.CostOfLiving].value as string),
    addressData: mapAddresses(
      {...formState[FormNames.AddressInfoForm].fieldsData, ...formState[AdditionalFormNames.CorrespondenceAddressForm]?.fieldsData}, 
      (formState.globalFormConfig.currentOpenedAdditionalForms.includes(AdditionalFormNames.CorrespondenceAddressForm))
   ),
    incomeData: mapIncomeToModel(
      {...formState[FormNames.IncomeInfoForm].fieldsData, ...formState[AdditionalFormNames.AdditionalIncomeForm]?.fieldsData}, 
      (formState.globalFormConfig.currentOpenedAdditionalForms.includes(AdditionalFormNames.AdditionalIncomeForm ))
    ),
    contactData: contact, 
  }
}

export const hashRequestByHMAC = <Body>(body: Body, hmacKey: string): Array<string> | [] => {
    if (!body || typeof body !== 'string'){
       console.error("Nieprawidłowy parametr body: ", body)
       return []
    }
 
    if(!hmacKey) {
       console.error("Nie podano klucza HMAC")
       return []
    }
 
    return [CryptoJS.HmacSHA256(body, hmacKey).toString(CryptoJS.enc.Base64), 'HmacSHA256']
}

export class FormSubmitionService {
    // =============== URLS ======================
    private readonly _client: HttpClient;
    private readonly _consentsFetchURL = 'settings/consent'
    private readonly _submitFullUrlPath = '/application/submitFull'


    // private readonly _integratorApiInstance: AxiosInstance;
    private readonly _apiInstance: AxiosInstance;
    private readonly _errorStatus = [400, 404, 500];
    private readonly _authErrors = [401, 403];
    private readonly _ignoreErrorMessages = ["timeout of 2000ms exceeded"];

    public constructor() {
      this._client = new HttpClient(Environment.apiUrl);

      this._apiInstance = axios.create({
          baseURL: Environment.apiUrl,
      });
    }

    public async getConsents(): Promise<ConsentFetchModel> {
        return (await this._apiInstance.get<ConsentFetchModel>(this._consentsFetchURL)).data;
    }

    // >>> GŁÓWNA FUNKCJA MAPUJĄCA
    public mapFieldsToSubmitionModel(formState: AllFormsState, appConsents: IAppConsentsModel[], additionalLoanData: IAdditionalLoanDataModel): IFormSubmissionModel
    {
      const cookies = new Cookies()
      const affiliateData = cookies.get(STORAGEKEYS.FORM_AFFILIATE_DATA_COOKIE)

      const appConsentsToSend: IAppConsentsModelToSend[]  = mapConsentsToModel(appConsents);
      const formSubmition: IFormSubmissionModel = {
        loan: additionalLoanData,
        applicant: mapApplicantDataToModel(formState),
        consents: appConsentsToSend,
        hdim: additionalLoanData.hdim, //[ZAŁOŻENIE BIZNESOWE: 83031] GDY UŻYTKOWNIK PRZECHODZI PROCES ZA POMOCĄ FORMULARZA OPENY, WYSYŁAMY HDIM I JSC. W PRZYPADKU PROCESU INTEGRATORA ZEWNĘTRZNEGO WYSYŁAMY TE DANE NA INNYM ETAPIE
        jsc: additionalLoanData.jsc,
        fillTime: additionalLoanData.fillTime,
      }

      if(!isEmpty(affiliateData)) { // Jeśli udało się odczytać dane z ciasteczka `FORM_AFFILIATE_DATA_COOKIE`, dołączamy je do wniosku
        formSubmition.affiliate = affiliateData
      }
      
      return formSubmition

    }

    public async submit(application: IFormSubmissionModel): Promise<ISubmitFormResponse> {
      return (await this._client.post<ISubmitFormResponse, IFormSubmissionModel>(
        this._submitFullUrlPath,
        application,
        true,
        undefined,
        { sendFacebookTrackingHeaders: true }
      )).data;
    }
}

// Wysyłanie aplikacji przy użyciu hashowania algorytmem HMAC
    // public sendApplication<T, Body>(body: Body): Promise<AxiosResponse<T>> {
    
    //     const headers: {[key: string]: any} = { 
    //       "content-type": "application/json",
    //     }
    
    //     const [signature, hashAlgorithm]  = hashRequestByHMAC(body, this._HMAC_KEY);
    
    //     headers["ei-signature-broker"] = this._RECIVER_NAME_FOR_FRONT;
    //     if (signature && hashAlgorithm) {
    //       headers["ei-signature"] = signature;
    //       headers["ei-signature-alg"] = hashAlgorithm;
    //     }
    //     else{
    //       console.error("Nie udało się utworzyć sygnatury dla żądania, lub nie podano funkcji hashującej");
    //     }
    
    //     return this._integratorApiInstance.post(this._formSubmitionUrl, body, {
    //       headers,
    //       withCredentials : true,
    //     });
    //   }